/**
 * Strip data that doesn't affect rendering from a FloorPlanRoom, so the
 * serialized form contains only meaningful content:
 *
 * - empty containers: edges holding nothing but an empty `segments` list, an
 *   empty `freeElements` array, empty corner `segments`, recursively (alcove
 *   walls, alcove outer corners, free-element corners)
 * - corners that are effectively square (both chamfer legs under half an
 *   inch) and hold nothing besides their legs
 * - optional fields holding the value they read as when left out: the
 *   `..._FIELD_DEFAULTS` tables beside their readers (`schemas/access.ts`),
 *   and each free element type's own (`freeElementTypes`), such as an
 *   object's `notBuiltIn: false`
 * - fields irrelevant to the item's type: alcove-only structure on
 *   doors/openings/windows, `swing` on alcoves/openings/windows. An edge
 *   segment of a type this version doesn't know keeps all its fields, since a
 *   newer version may use them.
 *
 * Purely structural: the room renders and measures identically, and every
 * field this version doesn't know is kept. Effectively pure: the input is
 * never mutated. Structural sharing: any node with nothing to remove is
 * returned as-is, so an already-optimal room comes back reference-identical.
 */
import { freeElementType } from './freeElementTypes';
import {
  ALCOVE_EDGE_NAMES,
  CORNER_NAMES,
  EDGE_NAMES,
  FloorPlanCorner,
  FloorPlanEdgeSegment,
  FloorPlanEdgeSegmentSimple,
  FloorPlanEdgeSimple,
  FloorPlanFreeElement,
  FloorPlanRoom,
  FREE_ELEMENT_FIELD_DEFAULTS,
  isKnownEdgeSegmentType,
  OUTER_WHICH,
  ROOM_FIELD_DEFAULTS,
  SEGMENT_FIELD_DEFAULTS,
} from './schemas';

/** Chamfer legs below half an inch read as a square corner. */
const MIN_CHAMFER_FT = 0.5 / 12;

/** Alcove-only structure: meaningless on doors, openings, and windows. */
const ALCOVE_ONLY_KEYS = ['depth', 'backLeftCorner', 'backRightCorner', 'leftEdge', 'backEdge', 'rightEdge'];

/** Map preserving array identity when no element changed. */
const mapShared = <T>(items: T[], fn: (item: T) => T): T[] => {
  let out: T[] | undefined;
  items.forEach((item, i) => {
    const next = fn(item);
    if (next !== item && !out) out = [...items];
    if (out) out[i] = next;
  });
  return out ?? items;
};

/** `node` without the `keys` it holds a value for, or `node` itself when it holds none of them. */
const withoutFields = <Node extends object>(node: Node, keys: readonly string[]): Node => {
  const held = keys.filter(key => (node as Record<string, unknown>)[key] !== undefined);
  if (!held.length) return node;
  const out = { ...node } as Record<string, unknown>;
  for (const key of held) delete out[key];
  return out as Node;
};

/** Whether `node` holds a value for no field but `keys`. */
const holdsOnly = (node: object, keys: readonly string[]): boolean => Object.entries(node).every(([key, value]) => value === undefined || keys.includes(key));

/** `node` without the fields that hold the value they read as when left out. */
const withoutDefaults = <Node extends object>(node: Node, defaults: Readonly<Record<string, unknown>>): Node =>
  withoutFields(node, Object.keys(defaults).filter(key => (node as Record<string, unknown>)[key] === defaults[key]));

/**
 * `node` with each of its slots `names` passed through `optimizeSlot`, which
 * gives back the slot's value itself, a smaller one, or undefined to drop the
 * slot. `node` itself comes back when no slot changes.
 */
function withOptimizedSlots<Node extends object, Value>(node: Node, names: readonly string[], optimizeSlot: (value: Value) => Value | undefined): Node {
  let out: Record<string, unknown> | undefined;
  for (const name of names) {
    const value = (node as Record<string, unknown>)[name] as Value | undefined;
    if (!value) continue;
    const optimized = optimizeSlot(value);
    if (optimized === value) continue;
    out ??= { ...node } as Record<string, unknown>;
    if (optimized === undefined) delete out[name];
    else out[name] = optimized;
  }
  return (out ?? node) as Node;
}

/** A corner with no `segments` list when it holds none. */
const optimizeCorner = (corner: FloorPlanCorner): FloorPlanCorner => {
  if (!corner.segments) return corner;
  if (corner.segments.length === 0) {
    const { segments: _empty, ...rest } = corner;
    return rest;
  }
  const segments = mapShared(corner.segments, optimizeSimpleSegment);
  return segments === corner.segments ? corner : { ...corner, segments };
};

/**
 * An optional corner slot, dropped when it is effectively square (both legs
 * under half an inch) and holds nothing besides its legs: no segments, and no
 * field a newer version added.
 */
const optimizeOptionalCorner = (corner: FloorPlanCorner): FloorPlanCorner | undefined => {
  const optimized = optimizeCorner(corner);
  const effectivelySquare = Math.abs(corner.dx) < MIN_CHAMFER_FT && Math.abs(corner.dy) < MIN_CHAMFER_FT;
  return effectivelySquare && holdsOnly(optimized, ['dx', 'dy']) ? undefined : optimized;
};

/** An edge slot whose segments each pass through `optimizeSegment`, dropped when it holds nothing but an empty segment list. */
const edgeOptimizer =
  <Edge extends FloorPlanEdgeSimple>(optimizeSegment: (segment: FloorPlanEdgeSegment) => FloorPlanEdgeSegment) => (edge: Edge): Edge | undefined => {
    if (edge.segments.length === 0 && holdsOnly(edge, ['segments'])) return undefined;
    const segments = mapShared(edge.segments, optimizeSegment);
    return segments === edge.segments ? edge : { ...edge, segments };
  };

/** `swing` means something only on a door; a type this version doesn't know keeps it. */
const swingIsIrrelevant = (segment: FloorPlanEdgeSegmentSimple): boolean =>
  segment.edgeSegmentType !== 'door' && isKnownEdgeSegmentType(segment.edgeSegmentType);

function optimizeSimpleSegment<Segment extends FloorPlanEdgeSegmentSimple>(segment: Segment): Segment {
  return withoutFields(withoutDefaults(segment, SEGMENT_FIELD_DEFAULTS), swingIsIrrelevant(segment) ? ['swing'] : []);
}

const optimizeAlcoveWalls = edgeOptimizer(optimizeSimpleSegment);

function optimizeSegment(segment: FloorPlanEdgeSegment): FloorPlanEdgeSegment {
  const simple = optimizeSimpleSegment(segment);
  if (segment.edgeSegmentType === 'alcove') {
    return withOptimizedSlots(withOptimizedSlots(simple, ALCOVE_EDGE_NAMES, optimizeAlcoveWalls), OUTER_WHICH, optimizeOptionalCorner);
  }
  return withoutFields(simple, isKnownEdgeSegmentType(segment.edgeSegmentType) ? ALCOVE_ONLY_KEYS : []);
}

const optimizeFreeElement = (element: FloorPlanFreeElement): FloorPlanFreeElement =>
  withoutDefaults(withOptimizedSlots(element, CORNER_NAMES, optimizeOptionalCorner), {
    ...FREE_ELEMENT_FIELD_DEFAULTS,
    ...freeElementType(element).fieldDefaults,
  });

const optimizeFreeElements = (elements: FloorPlanFreeElement[]): FloorPlanFreeElement[] | undefined =>
  elements.length === 0 ? undefined : mapShared(elements, optimizeFreeElement);

const optimizePrimaryEdge = edgeOptimizer(optimizeSegment);

export function getOptimizedFloorPlan(room: FloorPlanRoom): FloorPlanRoom {
  const structure = withOptimizedSlots(withOptimizedSlots(room, EDGE_NAMES, optimizePrimaryEdge), CORNER_NAMES, optimizeOptionalCorner);
  return withoutDefaults(withOptimizedSlots(structure, ['freeElements'], optimizeFreeElements), ROOM_FIELD_DEFAULTS);
}
